import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { api, getStoredToken, API } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { GlassCard } from "@/components/common/GlassCard";
import { PageHeader } from "@/components/common/PageHeader";
import { LoadingState, EmptyState } from "@/components/common/States";
import { StatusPill } from "@/components/common/StatusPill";
import { PlayerSubNav } from "@/components/common/PlayerSubNav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { toast } from "sonner";
import {
  FileText, Plus, Trash2, Download, RefreshCw, CheckCircle2, XCircle, Pencil, Save,
  X, Copy, ChevronUp, ChevronDown, Lock, Unlock, Wand2, Check, Sparkles, FileDown,
  History, Eye,
} from "lucide-react";

function useResolvedPlayerId() {
  const { user } = useAuth();
  const { playerId } = useParams();
  return playerId || user?.player_id;
}

function StatusBadge({ status }) {
  const s = status || "draft";
  const tone = s === "approved" ? "emerald" : s === "archived" ? "neutral" : "amber";
  return <StatusPill tone={tone} data-testid={`report-status-${s}`}>{s}</StatusPill>;
}

function NewReportDialog({ open, onOpenChange, onCreate, busy, types, sections }) {
  const [rtype, setRtype] = useState("scouting");
  const [title, setTitle] = useState("");
  const [notes, setNotes] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [customKinds, setCustomKinds] = useState(null); // null = use type defaults

  const typeDef = useMemo(() => types.find((t) => t.key === rtype), [types, rtype]);
  const defaultKinds = typeDef?.default_sections || [];
  const effectiveKinds = customKinds ?? defaultKinds;

  const toggleKind = (k) => {
    const list = [...effectiveKinds];
    const i = list.indexOf(k);
    if (i >= 0) list.splice(i, 1);
    else list.push(k);
    setCustomKinds(list);
  };

  const isSelected = (k) => effectiveKinds.includes(k);

  const submit = () => {
    const body = {
      type: rtype,
      title: title.trim() || undefined,
      advisor_notes_md: notes.trim() || undefined,
      date_range_from: dateFrom || undefined,
      date_range_to: dateTo || undefined,
    };
    if (customKinds !== null) body.section_kinds = customKinds;
    onCreate(body);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="glass-strong border-white/10 max-w-2xl text-1" data-testid="report-new-dialog">
        <DialogHeader>
          <DialogTitle>Generate a new report</DialogTitle>
          <DialogDescription className="text-3 text-[12px]">Pick a type, optional date range, and toggle sections to include. You can refine content after it's generated.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-3 text-[11px] uppercase tracking-wider">Report type</Label>
            <Select value={rtype} onValueChange={(v) => { setRtype(v); setCustomKinds(null); }}>
              <SelectTrigger className="bg-white/5 border-white/10 text-1 mt-1.5" data-testid="report-new-type"><SelectValue /></SelectTrigger>
              <SelectContent className="glass-strong border-white/10 text-1">
                {types.map((t) => (
                  <SelectItem key={t.key} value={t.key} data-testid={`report-new-type-${t.key}`}>
                    <div className="flex flex-col">
                      <span className="text-1">{t.label}</span>
                      <span className="text-3 text-[11px]">{t.description}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-3 text-[11px] uppercase tracking-wider">Title (optional — defaults to type label)</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder={typeDef?.label || ""} className="bg-white/5 border-white/10 text-1 mt-1.5" data-testid="report-new-title" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-3 text-[11px] uppercase tracking-wider">Date range from</Label>
              <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="bg-white/5 border-white/10 text-1 mt-1.5" data-testid="report-new-from" />
            </div>
            <div>
              <Label className="text-3 text-[11px] uppercase tracking-wider">Date range to</Label>
              <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="bg-white/5 border-white/10 text-1 mt-1.5" data-testid="report-new-to" />
            </div>
          </div>
          <div>
            <Label className="text-3 text-[11px] uppercase tracking-wider">Advisor notes (optional)</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} className="bg-white/5 border-white/10 text-1 mt-1.5 min-h-[70px]" placeholder="Context to include in the report…" data-testid="report-new-notes" />
          </div>
          <div>
            <Label className="text-3 text-[11px] uppercase tracking-wider">Sections to include</Label>
            <div className="mt-2 grid grid-cols-2 gap-1.5 max-h-[220px] overflow-y-auto pr-1">
              {defaultKinds.map((k) => (
                <label key={k} className="flex items-center gap-2 glass px-2.5 py-1.5 cursor-pointer" data-testid={`report-new-section-${k}`}>
                  <Checkbox checked={isSelected(k)} onCheckedChange={() => toggleKind(k)} className="border-cyan-300/60 data-[state=checked]:bg-cyan-400 data-[state=checked]:text-[#04111A]" />
                  <span className="text-2 text-[12px]">{sections[k]?.title || k}</span>
                </label>
              ))}
            </div>
            {customKinds !== null && <Button variant="ghost" size="sm" onClick={() => setCustomKinds(null)} className="text-3 hover:text-white mt-1.5">Reset to defaults</Button>}
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-2">Cancel</Button>
          <Button onClick={submit} disabled={busy} className="cta-primary" data-testid="report-new-confirm">
            <FileText className="h-4 w-4" /> {busy ? "Generating…" : "Generate report"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SectionCard({ section, index, total, isAdmin, onChange, onMove, onToggleLock, onToggleInclude }) {
  const [editing, setEditing] = useState(false);
  const [body, setBody] = useState(section.body_md || "");
  useEffect(() => { setBody(section.body_md || ""); }, [section.id, section.body_md]);
  const locked = !!section.locked;
  const included = section.included !== false;
  const isCompliance = section.kind === "compliance_footer";

  return (
    <GlassCard className={`p-4 ${!included ? "opacity-50" : ""}`} testId={`report-section-${section.id}`}>
      <div className="flex items-center gap-2 flex-wrap">
        {isAdmin && (
          <Checkbox
            checked={included}
            onCheckedChange={() => onToggleInclude(section)}
            className="border-white/40 data-[state=checked]:bg-cyan-400 data-[state=checked]:text-[#04111A]"
            data-testid={`report-section-include-${section.id}`}
          />
        )}
        <span className="text-1 text-[15px] font-semibold flex-1">{section.title}</span>
        {isCompliance && <StatusPill tone="violet">Footer · required</StatusPill>}
        {locked && <StatusPill tone="amber"><Lock className="h-3 w-3 inline" /> locked</StatusPill>}
        {isAdmin && (
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="sm" disabled={index === 0} onClick={() => onMove(section, -1)} className="text-3 hover:text-white" data-testid={`report-section-up-${section.id}`}><ChevronUp className="h-3 w-3" /></Button>
            <Button variant="ghost" size="sm" disabled={index === total - 1} onClick={() => onMove(section, 1)} className="text-3 hover:text-white" data-testid={`report-section-down-${section.id}`}><ChevronDown className="h-3 w-3" /></Button>
            <Button variant="ghost" size="sm" onClick={() => onToggleLock(section)} className={`text-3 hover:text-white ${locked ? "text-amber-300" : ""}`} data-testid={`report-section-lock-${section.id}`}>
              {locked ? <Unlock className="h-3 w-3" /> : <Lock className="h-3 w-3" />}
            </Button>
            {!editing && !isCompliance && (
              <Button variant="ghost" size="sm" onClick={() => setEditing(true)} className="text-3 hover:text-white" data-testid={`report-section-edit-${section.id}`}><Pencil className="h-3 w-3" /></Button>
            )}
          </div>
        )}
      </div>
      {!editing ? (
        <div className="markdown-body markdown-compact text-2 text-[13.5px] mt-3 leading-relaxed">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{section.body_md || "_Empty section._"}</ReactMarkdown>
        </div>
      ) : (
        <div className="mt-3 space-y-2">
          <Textarea value={body} onChange={(e) => setBody(e.target.value)} className="bg-white/5 border-white/10 text-1 min-h-[180px] text-[13px] font-mono" data-testid={`report-section-textarea-${section.id}`} />
          <div className="flex gap-2">
            <Button size="sm" className="cta-primary" data-testid={`report-section-save-${section.id}`}
              onClick={async () => { await onChange(section, { body_md: body, locked: true }); setEditing(false); }}>
              <Save className="h-3 w-3" /> Save & lock
            </Button>
            <Button size="sm" variant="ghost" onClick={() => { setBody(section.body_md || ""); setEditing(false); }} className="text-3"><X className="h-3 w-3" /> Cancel</Button>
          </div>
        </div>
      )}
    </GlassCard>
  );
}

function AIDraftPanel({ report, sections, onApply, onReject, onGenerate, busy }) {
  const draft = report.ai_draft;
  const draftSections = draft?.sections || [];
  const [selectedKinds, setSelectedKinds] = useState(null);
  const draftBodyByKind = useMemo(() => Object.fromEntries(draftSections.map((s) => [s.kind, s.body_md])), [draftSections]);

  if (!draft || !draftSections.length) {
    return (
      <GlassCard className="p-4 ring-1 ring-violet-300/25" testId="report-ai-empty">
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-violet-200 flex items-center gap-1.5"><Sparkles className="h-3 w-3" /> AI polish</div>
            <div className="text-2 text-[13px] mt-1">Generate a polished draft across all included sections. You'll review before anything overwrites the report.</div>
          </div>
          <Button onClick={onGenerate} disabled={busy} className="cta-primary" data-testid="report-ai-generate">
            <Wand2 className="h-4 w-4" /> {busy ? "Generating…" : "Generate AI draft"}
          </Button>
        </div>
      </GlassCard>
    );
  }

  const kindsToApply = selectedKinds ?? draftSections.map((s) => s.kind);
  const toggleKind = (k) => {
    const list = [...kindsToApply];
    const i = list.indexOf(k);
    if (i >= 0) list.splice(i, 1);
    else list.push(k);
    setSelectedKinds(list);
  };

  return (
    <GlassCard className="p-4 ring-1 ring-amber-300/30" testId="report-ai-draft">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="text-[10px] uppercase tracking-wider text-amber-200 flex items-center gap-1.5"><Wand2 className="h-3 w-3" /> AI draft · pending approval</div>
        {draft.generated_at && <span className="text-3 text-[10px]">{new Date(draft.generated_at).toLocaleString()}</span>}
      </div>
      {draft.advisor_note_md && (
        <div className="glass p-3 mt-2">
          <div className="text-[10px] uppercase tracking-wider text-cyan-200">Suggested advisor note</div>
          <div className="markdown-body text-2 text-[12.5px] mt-1"><ReactMarkdown remarkPlugins={[remarkGfm]}>{draft.advisor_note_md}</ReactMarkdown></div>
        </div>
      )}
      <div className="space-y-3 mt-3">
        {draftSections.map((s) => {
          const sec = sections.find((x) => x.kind === s.kind);
          const include = kindsToApply.includes(s.kind);
          return (
            <div key={s.kind} className="glass p-3" data-testid={`report-ai-section-${s.kind}`}>
              <div className="flex items-center gap-2">
                <Checkbox checked={include} onCheckedChange={() => toggleKind(s.kind)} className="border-amber-300/60 data-[state=checked]:bg-amber-400 data-[state=checked]:text-[#04111A]" data-testid={`report-ai-apply-check-${s.kind}`} />
                <span className="text-1 text-[13px] font-semibold">{sec?.title || s.kind}</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
                <div>
                  <div className="text-3 text-[10px] uppercase tracking-wider">Current</div>
                  <div className="markdown-body text-2 text-[12.5px] mt-1 max-h-[160px] overflow-y-auto pr-1"><ReactMarkdown remarkPlugins={[remarkGfm]}>{sec?.body_md || "_(empty)_"}</ReactMarkdown></div>
                </div>
                <div>
                  <div className="text-3 text-[10px] uppercase tracking-wider text-amber-200">AI polished</div>
                  <div className="markdown-body text-2 text-[12.5px] mt-1 max-h-[160px] overflow-y-auto pr-1"><ReactMarkdown remarkPlugins={[remarkGfm]}>{s.body_md}</ReactMarkdown></div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <div className="flex flex-wrap gap-2 mt-3">
        <Button onClick={() => onApply(kindsToApply)} disabled={busy || kindsToApply.length === 0} className="cta-primary" data-testid="report-ai-apply">
          <Check className="h-4 w-4" /> Apply {kindsToApply.length} section{kindsToApply.length === 1 ? "" : "s"}
        </Button>
        <Button onClick={onReject} disabled={busy} variant="ghost" className="text-rose-200 hover:text-rose-100" data-testid="report-ai-reject">
          <X className="h-4 w-4" /> Discard draft
        </Button>
        <Button onClick={onGenerate} disabled={busy} variant="ghost" className="text-3 hover:text-white" data-testid="report-ai-regenerate">
          <Wand2 className="h-4 w-4" /> Regenerate
        </Button>
      </div>
    </GlassCard>
  );
}

function ReportHeader({ report, types, isAdmin, onApprove, onReject, onNewVersion, onDelete, onRegenerate, onExportPdf, busy }) {
  const typeDef = types.find((t) => t.key === report.type);
  const approved = report.status === "approved";
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 flex-wrap">
        <StatusPill tone="cyan">{typeDef?.label || report.type}</StatusPill>
        <StatusBadge status={report.status} />
        <StatusPill tone="neutral" data-testid="report-version">v{report.version || 1}</StatusPill>
        {report.approved_at && <span className="text-3 text-[11px]">Approved {new Date(report.approved_at).toLocaleString()}{report.approved_by ? ` · ${report.approved_by}` : ""}</span>}
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        {isAdmin && !approved && (
          <Button onClick={onApprove} disabled={busy} className="cta-primary" data-testid="report-approve">
            <CheckCircle2 className="h-4 w-4" /> Approve & publish
          </Button>
        )}
        {isAdmin && approved && (
          <Button onClick={onReject} disabled={busy} variant="ghost" className="text-rose-200 hover:text-rose-100" data-testid="report-unapprove">
            <XCircle className="h-4 w-4" /> Unapprove
          </Button>
        )}
        {isAdmin && (
          <>
            <Button onClick={onRegenerate} disabled={busy} variant="ghost" className="text-3 hover:text-white" data-testid="report-regenerate">
              <RefreshCw className="h-4 w-4" /> Regenerate from live data
            </Button>
            <Button onClick={onNewVersion} disabled={busy} variant="ghost" className="text-3 hover:text-white" data-testid="report-new-version">
              <Copy className="h-4 w-4" /> New version
            </Button>
          </>
        )}
        <Button onClick={onExportPdf} disabled={busy || (!isAdmin && !approved)} variant="ghost" className="text-2 hover:text-white disabled:opacity-40" data-testid="report-export-pdf">
          <FileDown className="h-4 w-4" /> {approved || isAdmin ? "Export PDF" : "Export (approved only)"}
        </Button>
        {isAdmin && (
          <Button onClick={onDelete} disabled={busy} variant="ghost" className="text-rose-200 hover:text-rose-100 ml-auto" data-testid="report-delete">
            <Trash2 className="h-4 w-4" /> Delete
          </Button>
        )}
      </div>
    </div>
  );
}

export default function ReportsPage() {
  const { user } = useAuth();
  const playerId = useResolvedPlayerId();
  const isAdmin = user?.role === "admin";

  const [config, setConfig] = useState({ types: [], sections: {}, compliance_footer: "" });
  const [reports, setReports] = useState([]);
  const [active, setActive] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    if (!playerId) { setLoading(false); return; }
    setLoading(true);
    try {
      const [cfg, lst] = await Promise.all([
        api.get("/reports/config/types"),
        api.get("/reports", { params: { player_id: playerId } }),
      ]);
      setConfig(cfg.data);
      const all = lst.data || [];
      const filtered = isAdmin ? all : all.filter((r) => r.status === "approved");
      setReports(filtered);
      setActive((curr) => curr && filtered.find((x) => x.id === curr.id) ? filtered.find((x) => x.id === curr.id) : (filtered[0] || null));
    } catch (e) { toast.error("Failed to load reports"); } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [playerId]);

  // Group by chain root → show version ladder
  const chains = useMemo(() => {
    const byRoot = new Map();
    for (const r of reports) {
      const root = r.parent_report_id || r.id;
      if (!byRoot.has(root)) byRoot.set(root, []);
      byRoot.get(root).push(r);
    }
    const groups = [];
    for (const arr of byRoot.values()) {
      arr.sort((a, b) => (b.version || 1) - (a.version || 1));
      groups.push(arr);
    }
    groups.sort((a, b) => new Date(b[0].created_at) - new Date(a[0].created_at));
    return groups;
  }, [reports]);

  const replaceReport = (updated) => {
    setReports((arr) => arr.map((x) => x.id === updated.id ? updated : x));
    if (active?.id === updated.id) setActive(updated);
  };

  const createReport = async (body) => {
    setBusy(true);
    try {
      const r = await api.post("/reports", { ...body, player_id: playerId });
      setReports((arr) => [r.data, ...arr]);
      setActive(r.data);
      setShowCreate(false);
      toast.success("Report generated");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };

  const patchSection = async (section, fields) => {
    try {
      const r = await api.patch(`/reports/${active.id}`, { sections: [{ id: section.id, ...fields }] });
      replaceReport(r.data);
      toast.success("Section saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const moveSection = async (section, dir) => {
    const list = [...(active.sections || [])].sort((a, b) => (a.order || 0) - (b.order || 0));
    const idx = list.findIndex((s) => s.id === section.id);
    const j = idx + dir;
    if (j < 0 || j >= list.length) return;
    [list[idx], list[j]] = [list[j], list[idx]];
    const patches = list.map((s, i) => ({ id: s.id, order: i }));
    try {
      const r = await api.patch(`/reports/${active.id}`, { sections: patches });
      replaceReport(r.data);
    } catch (e) { toast.error("Reorder failed"); }
  };

  const toggleLock = async (section) => await patchSection(section, { locked: !section.locked });
  const toggleInclude = async (section) => await patchSection(section, { included: !(section.included !== false) });

  const approve = async () => {
    setBusy(true);
    try { const r = await api.post(`/reports/${active.id}/approve`); replaceReport(r.data); toast.success("Report approved"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  const unapprove = async () => {
    setBusy(true);
    try { const r = await api.post(`/reports/${active.id}/reject`); replaceReport(r.data); toast.success("Report unapproved"); }
    catch (e) { toast.error("Failed"); } finally { setBusy(false); }
  };
  const regenerate = async () => {
    if (!window.confirm("Re-pull live data? Locked sections are preserved. Current approval will be reset.")) return;
    setBusy(true);
    try { const r = await api.post(`/reports/${active.id}/regenerate`); replaceReport(r.data); toast.success("Regenerated"); }
    catch (e) { toast.error("Failed"); } finally { setBusy(false); }
  };
  const newVersion = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/reports/${active.id}/new-version`);
      setReports((arr) => [r.data, ...arr]); setActive(r.data); toast.success(`v${r.data.version} created`);
    } catch (e) { toast.error("Failed"); } finally { setBusy(false); }
  };
  const deleteReport = async () => {
    if (!window.confirm(`Delete "${active.title}" v${active.version || 1}? This cannot be undone.`)) return;
    setBusy(true);
    try {
      await api.delete(`/reports/${active.id}`);
      setReports((arr) => arr.filter((r) => r.id !== active.id));
      setActive(null);
      toast.success("Deleted");
    } catch (e) { toast.error("Failed"); } finally { setBusy(false); }
  };

  const aiGenerate = async () => {
    setBusy(true);
    try {
      toast.loading("Generating AI draft…", { id: "aidraft" });
      const r = await api.post(`/reports/${active.id}/ai-draft`);
      toast.dismiss("aidraft");
      const count = (r.data?.ai_draft?.sections || []).length;
      if (count === 0) {
        toast.error("AI returned no content — please try regenerating.");
      } else {
        toast.success(`AI draft ready — ${count} section${count === 1 ? "" : "s"}`);
      }
      replaceReport(r.data);
    } catch (e) { toast.dismiss("aidraft"); toast.error(e?.response?.data?.detail || "AI failed — try again"); } finally { setBusy(false); }
  };
  const aiApply = async (kinds) => {
    setBusy(true);
    try { const r = await api.post(`/reports/${active.id}/ai-apply`, { kinds }); replaceReport(r.data); toast.success("AI draft applied"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  const aiReject = async () => {
    setBusy(true);
    try { await api.post(`/reports/${active.id}/ai-reject`); replaceReport({ ...active, ai_draft: null }); toast.success("Draft discarded"); }
    catch { toast.error("Failed"); } finally { setBusy(false); }
  };

  const exportPdf = async () => {
    if (!active) return;
    setBusy(true);
    try {
      const token = getStoredToken();
      const res = await fetch(`${API}/reports/${active.id}/export.pdf`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `OmniSportsGroup_${(active.title || "Report").replace(/\s+/g, "_")}_v${active.version || 1}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("PDF downloaded");
    } catch (e) { toast.error("Export failed"); } finally { setBusy(false); }
  };

  const sortedSections = useMemo(() => [...(active?.sections || [])].sort((a, b) => (a.order || 0) - (b.order || 0)), [active]);

  if (loading) return <LoadingState />;

  return (
    <div className="space-y-5 max-w-[1500px] mx-auto">
      <PlayerSubNav playerId={playerId} />
      <PageHeader
        title="Reports"
        subtitle="Typed, versioned reports with AI drafting and premium PDF export."
        actions={isAdmin ? (
          <Button data-testid="report-create" className="cta-primary" onClick={() => setShowCreate(true)}>
            <Plus className="h-4 w-4" /> New report
          </Button>
        ) : null}
      />

      {reports.length === 0 ? (
        <EmptyState icon={FileText} title="No reports yet" description={isAdmin ? "Click 'New report' to generate the first one." : "Reports from your advisor will show here once approved."} />
      ) : (
        <div className="grid grid-cols-12 gap-5">
          {/* Left: chain list */}
          <GlassCard className="col-span-12 lg:col-span-4 p-3" testId="report-list">
            <div className="max-h-[78vh] overflow-y-auto pr-1 space-y-2">
              {chains.map((chain) => {
                const latest = chain[0];
                return (
                  <div key={latest.parent_report_id || latest.id} className="space-y-1">
                    {chain.map((r, i) => (
                      <button
                        key={r.id}
                        data-testid={`report-item-${r.id}`}
                        onClick={() => setActive(r)}
                        className={`w-full text-left rounded-xl p-3 ring-1 transition ${active?.id === r.id ? "ring-cyan-300/40 bg-white/[0.08]" : "ring-white/5 hover:bg-white/[0.04]"}`}
                      >
                        <div className="flex items-center gap-2">
                          <span className="text-1 text-[13px] font-medium flex-1 line-clamp-1">{r.title}</span>
                          <StatusBadge status={r.status} />
                          <span className="text-3 text-[10px]">v{r.version || 1}</span>
                        </div>
                        <div className="text-3 text-[11px] mt-0.5 flex items-center gap-1.5">
                          <StatusPill tone="neutral">{config.types.find((t) => t.key === r.type)?.label || r.type}</StatusPill>
                          <span>· {new Date(r.updated_at || r.created_at).toLocaleDateString()}</span>
                        </div>
                        {i === 0 && chain.length > 1 && (
                          <div className="text-3 text-[10px] mt-1 flex items-center gap-1"><History className="h-3 w-3" /> {chain.length} versions in chain</div>
                        )}
                      </button>
                    ))}
                  </div>
                );
              })}
            </div>
          </GlassCard>

          {/* Right: report detail */}
          <div className="col-span-12 lg:col-span-8 space-y-4">
            {!active ? (
              <GlassCard className="p-8"><div className="text-2 text-sm">Select a report.</div></GlassCard>
            ) : (
              <>
                <GlassCard className="p-5" testId="report-detail">
                  <h2 className="text-1 text-2xl font-semibold">{active.title}</h2>
                  <div className="text-3 text-[12px] mt-1">Created {new Date(active.created_at).toLocaleString()}{active.created_by ? ` by ${active.created_by}` : ""}</div>
                  <div className="mt-3">
                    <ReportHeader
                      report={active}
                      types={config.types}
                      isAdmin={isAdmin}
                      onApprove={approve}
                      onReject={unapprove}
                      onRegenerate={regenerate}
                      onNewVersion={newVersion}
                      onDelete={deleteReport}
                      onExportPdf={exportPdf}
                      busy={busy}
                    />
                  </div>
                </GlassCard>

                {isAdmin && (
                  <AIDraftPanel
                    report={active}
                    sections={sortedSections}
                    onApply={aiApply}
                    onReject={aiReject}
                    onGenerate={aiGenerate}
                    busy={busy}
                  />
                )}

                {/* Sections */}
                <div className="space-y-3">
                  {sortedSections.map((s, i) => (
                    <SectionCard
                      key={s.id}
                      section={s}
                      index={i}
                      total={sortedSections.length}
                      isAdmin={isAdmin}
                      onChange={patchSection}
                      onMove={moveSection}
                      onToggleLock={toggleLock}
                      onToggleInclude={toggleInclude}
                    />
                  ))}
                </div>

                {/* Compliance disclaimer */}
                {active.compliance_footer && (
                  <GlassCard className="p-3" testId="report-compliance">
                    <div className="text-3 text-[11px] italic">{active.compliance_footer}</div>
                  </GlassCard>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {isAdmin && (
        <NewReportDialog
          open={showCreate}
          onOpenChange={setShowCreate}
          onCreate={createReport}
          busy={busy}
          types={config.types}
          sections={config.sections}
        />
      )}
    </div>
  );
}
